import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'
import { useTranslate } from 'react-polyglot'
import processEnvPublic from 'unlikely-env/public'
import { useDebounce } from 'use-debounce'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { FACETS_TO_RETRIEVE, TRACKING_EVENTS } from '~/lib/constants'

import {
  AlgoliaSearchProvider,
  useAlgoliaQueryContext,
  useAlgoliaSearch,
} from '@unlikelystudio/react-ecommerce-hooks'
import { useIsMountedState } from '@unlikelystudio/react-hooks'

import SearchInput from '~/components/Search/SearchInput'
import SearchKeys from '~/components/Search/SearchKeys'
import CardProduct, { CardProductProps } from '~/components/UI/CardProduct'
import { InlineCtaProps } from '~/components/UI/InlineCta'
import ResultsGrid, {
  CardProductAlgolia,
  RESULTS_GRID_LAYOUT,
} from '~/components/UI/ResultsGrid'
import SliderProducts from '~/components/UI/SliderProducts'

import { useStyle } from '~/providers/StyleProvider'
import { useTracker } from '~/providers/TrackerProvider'

import useTrackProductCardsList, {
  fromProductCardPropsToProductTrackingData,
} from '~/hooks/tracking/useTrackProductCardsList'
import useLocale from '~/hooks/useLocale'

import getAlgoliaProductIndex from '~/utils/get-algolia-product-index'
import { getQueryParams } from '~/utils/get-query-params'

import { SEARCH } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface AlgoliaInitialSearchResponse {
  params: {
    locale?: string
    index: string
    facetFilters: string[][]
    hitsPerPage?: number
    filters?: string
  }
  initialData: {
    pages: any[]
  }
}

export interface SearchProps {
  className?: string
  algoliaProps?: AlgoliaInitialSearchResponse
  children?: JSX.Element | JSX.Element[]
  keywords?: string[]
  items?: CardProductProps[]
  cta?: InlineCtaProps
}

export interface SearchDefaultScreenProps {
  className?: string
  keywords?: string[]
  items?: CardProductProps[]
}

export interface CustomNoResultProps {
  q?: string
  cta?: InlineCtaProps
}

export interface BestSellersProps {
  className?: string
  items?: CardProductProps[]
}

export function BestSellers({ className, items }: BestSellersProps) {
  const listName = SEARCH.BEST_SELLERS
  const t = useTranslate()

  const [setRef, handleItemTracker] = useTrackProductCardsList(listName, items)
  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Label18Primary,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })

  return (
    items?.length > 0 && (
      <div className={cx(css.BestSellers, className)} ref={setRef}>
        <div className={cx(css.titleContainer, gridStyle)}>
          <h3 className={cx(css.title, titleStyle)}>
            {t(SEARCH.BEST_SELLERS)}
          </h3>
        </div>
        {items?.length > 0 && (
          <div className={cx(css.products)}>
            <SliderProducts
              className={cx(css.slider, css.hideOnSmallScreen)}
              items={items}
              arrowsTheme={GlobalThemeColors.Black}
              handleItemTracker={handleItemTracker}
              withArrows={true}
            />
            <div className={cx(css.mobileGrid, css.hideOnLargeScreen)}>
              {items?.map((item, key) => (
                <CardProduct key={`bestseller_item_${key}`} {...item} />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  )
}

export function SearchDefaultScreen({
  keywords,
  items,
}: SearchDefaultScreenProps) {
  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })

  return (
    <>
      {keywords && (
        <div className={gridStyle}>
          <SearchKeys className={cx(css.searchKeys)} keywords={keywords} />
        </div>
      )}
      <BestSellers className={cx(css.bestSellers)} items={items} />
    </>
  )
}

export function NoResults({ keywords, items }: SearchDefaultScreenProps) {
  const t = useTranslate()
  const noResultsStyle = useStyle({
    textPreset: GlobalTextPreset.Title24_28Primary,
  })
  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  return (
    <div className={cx(css.NoResults)}>
      <div className={cx(css.titleContainer, gridStyle)}>
        <h3 className={cx(css.noResultsTitle, noResultsStyle)}>
          {t(SEARCH.NO_RESULT)}
        </h3>
      </div>
      <div className={cx(css.noResultsDefault)}>
        <SearchDefaultScreen keywords={keywords} items={items} />
      </div>
    </div>
  )
}

export function Search({ className, keywords, items, cta }: SearchProps) {
  const locale = useLocale()
  const router = useRouter()
  const { q } = useMemo(() => getQueryParams() ?? {}, [router])
  const [, setQuery] = useAlgoliaQueryContext()
  const tracker = useTracker()
  const mounted = useIsMountedState()

  const [debouncedValue] = useDebounce(q, 150)

  useEffect(() => {
    if (q) {
      if (!mounted) return
      const indexName = getAlgoliaProductIndex()
      const index = `${locale}_${indexName}`
      setQuery((prevState) => {
        return {
          ...prevState,
          query: debouncedValue,
          index,
        }
      })
    }
  }, [debouncedValue, locale, mounted])
  const { data } = useAlgoliaSearch<CardProductAlgolia>()

  const [debouncedAlgoliaData] = useDebounce(
    data?.pages?.[0]?.nbHits?.toString() ?? null,
    2000,
  )

  const memoizedDebouncedAlgoliaData = useMemo(
    () => debouncedAlgoliaData,
    [debouncedAlgoliaData],
  )

  useEffect(() => {
    if (memoizedDebouncedAlgoliaData) {
      tracker.emit(TRACKING_EVENTS.SEARCH, {
        results: data?.pages?.[0]?.hits?.map((product) =>
          fromProductCardPropsToProductTrackingData(product, {}),
        ),
        searchTerm: q,
        clickedUrl: q,
      })
    }
  }, [memoizedDebouncedAlgoliaData])

  const t = useTranslate()
  return (
    <section className={cx(css.Search, className)}>
      <SearchInput className={css.input} q={q} />
      {q?.length > 0 ? (
        <ResultsGrid
          className={cx(css.grid)}
          title={t(SEARCH.RESULTS)}
          layout={RESULTS_GRID_LAYOUT.search}
          topBarClassName={cx(css.topBar)}
          initialFacetFilters={[]}
          noResults={<NoResults keywords={keywords} items={items} />}
        />
      ) : (
        <section className={cx(css.SearchDefaultScreen)}>
          <SearchDefaultScreen keywords={keywords} items={items} />
        </section>
      )}
    </section>
  )
}

Search.defaultProps = {}

export default function SearchWrapper({
  children,
  algoliaProps,
  ...props
}: SearchProps) {
  const HITS_PER_PAGE = 16
  const test = {
    ...algoliaProps?.params,
    facets: FACETS_TO_RETRIEVE,
  }
  return (
    <>
      <AlgoliaSearchProvider
        appId={processEnvPublic('NEXT_PUBLIC_ALGOLIA_APP_ID')}
        apiKey={processEnvPublic('NEXT_PUBLIC_ALGOLIA_API_KEY')}
        initialSearchProps={{
          ...test,
          hitsPerPage: HITS_PER_PAGE,
        }}
        defaultQueryOptions={{
          refetchOnMount: false,
          initialData: {
            ...algoliaProps?.initialData,
            pageParams: [],
          },
        }}>
        <Search {...props}>{children}</Search>
      </AlgoliaSearchProvider>
    </>
  )
}
